import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue';

const MOBILE_QUERY = '(max-width: 959.98px)';

/**
 * Reactive mobile breakpoint watcher.
 *
 * Returns a `Ref<boolean>` that is `true` when the viewport width is below
 * 960px (matching the KdsNav mobile breakpoint). The value is read at mount
 * and stays in sync with the `change` event of the underlying `MediaQueryList`.
 *
 * SSR-safe: defaults to `false` when `window` / `matchMedia` are unavailable.
 * Cleans up the listener on unmount.
 *
 * @returns Reactive boolean indicating whether the viewport is in mobile range.
 */
export const useIsMobile = (): Ref<boolean> => {
  const isMobile = ref<boolean>(false);

  let mediaQuery: MediaQueryList | null = null;

  const handleChange = (event: MediaQueryListEvent) => {
    isMobile.value = event.matches;
  };

  onMounted(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    mediaQuery = window.matchMedia(MOBILE_QUERY);
    isMobile.value = mediaQuery.matches;
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
    } else if (typeof (mediaQuery as MediaQueryList).addListener === 'function') {
      // Safari < 14 fallback
      (mediaQuery as MediaQueryList).addListener(handleChange);
    }
  });

  onBeforeUnmount(() => {
    if (!mediaQuery) return;
    if (typeof mediaQuery.removeEventListener === 'function') {
      mediaQuery.removeEventListener('change', handleChange);
    } else if (typeof (mediaQuery as MediaQueryList).removeListener === 'function') {
      (mediaQuery as MediaQueryList).removeListener(handleChange);
    }
    mediaQuery = null;
  });

  return isMobile;
};
